import React, { useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';

import { Logger } from 'wtb-utils';
import { Button, Typography } from 'wtb-components';
import { ReactComponent as ErrorImage } from 'assets/images/account-auth-error.svg';
import { ReactComponent as BankIdLogo } from 'assets/images/bankid-plain.svg';
import { generatePayment } from 'api';
import {
  useAppContext,
  useDialogNavigationContext,
  useLocaleContext,
  useTransactionContext
} from 'context';
import { useHistory, useParams } from 'react-router-dom';
import {
  ANALYTICS_EVENTS, APP_STEPS, AUTOGIRO_STEPS, BETALINGSSERVICE_STEPS, PAYMENT_TYPES,
  SEPA_STEPS
} from 'constants.js';

import { useSendAnalyticsEvent } from 'hooks';
import styles from './Retry.module.css';

const Retry = () => {
  const history = useHistory();
  const { id } = useParams();

  const {
    setHasBack,
    setHasClose,
    setHasFooter,
    setTitle
  } = useDialogNavigationContext();
  const {
    payment,
    sign,
    setActions,
    setPayment,
    setSign,
    setTransaction,
    selectedPaymentPlan
  } = useTransactionContext();
  const { setAppStep } = useAppContext();
  const { translate, locale } = useLocaleContext();

  const [ isAutomaticFlowLoading, setIsAutomaticFlowLoading ] = useState(false);
  const [ isManualFlowLoading, setIsManualFlowLoading ] = useState(false);
  const sendAnalyticsEvent = useSendAnalyticsEvent();

  const isSepa = selectedPaymentPlan?.type === PAYMENT_TYPES.SEPA;
  const isAutogiro = selectedPaymentPlan?.type === PAYMENT_TYPES.AUTOGIRO;

  useEffect(() => {
    setHasBack(false);
    setHasClose(true);
    setHasFooter(true);
    setTitle('');
    sendAnalyticsEvent(ANALYTICS_EVENTS.RETRY, { retryType: ANALYTICS_EVENTS.RETRY_EVENT_PARAMS.ACCOUNT });
  }, [
    translate,
    setHasBack,
    setHasClose,
    setHasFooter,
    setTitle,
    sendAnalyticsEvent
  ]);

  const hasAutomaticFlow = payment?.launchUrl || sign?.request?.launchUrl;

  const handleAddAutomatic = () => {
    setIsAutomaticFlowLoading(true);

    if (payment?.launchUrl) {
      window.location.href = payment.launchUrl;
    } else if (sign?.request?.launchUrl) {
      window.location.href = sign.request.launchUrl;
    }

    setIsAutomaticFlowLoading(false);
  };

  const handleAddManual = () => {
    setIsManualFlowLoading(true);
    // setPaymentStep(AUTOGIRO_STEPS.ADD_BANK_ACCOUNT);
    // history.push(`/${id}/payment/${PAYMENT_TYPES.AUTOGIRO.toLowerCase()}/${AUTOGIRO_STEPS.MAIN_BANKS}`);

    const paymentType = selectedPaymentPlan?.type.replace('_MOCK', '').replace('_mock', '').toLowerCase();

    generatePayment(id, { planId: selectedPaymentPlan.id })
      .then((response) => {
        if (response.actions) setActions(response.actions);
        if (response.pay) setPayment(response.pay);
        if (response.sign) setSign(response.sign);

        if (!response.actions || !response.pay) {
          Sentry.captureException({
            type: 'debug',
            data: {
              id,
              planId: selectedPaymentPlan.id,
              language: locale,
              response: JSON.stringify(response)
            }
          });
        }

        setTransaction((previous) => ({
          ...previous,
          state: response.state
        }));

        switch (paymentType?.toUpperCase()) {
          case PAYMENT_TYPES.AUTOGIRO:
            history.push(`/${id}/payment/${paymentType}/${AUTOGIRO_STEPS.MAIN_BANKS}`);
            break;
          case PAYMENT_TYPES.BETALINGSSERVICE:
            history.push(`/${id}/payment/${paymentType}/${BETALINGSSERVICE_STEPS.MAIN_BANKS}`);
            break;
          case PAYMENT_TYPES.SEPA:
            history.push(`/${id}/payment/${paymentType}/${SEPA_STEPS.MAIN_BANKS}`);
            break;
          default:
            Logger.error(`Payment type '${paymentType}' redirection not configured`);
            history.push(`/${id}/payment/${paymentType}`);
            break;
        }
      })
      .catch((error) => {
        setIsManualFlowLoading(false);
        Sentry.captureException(error);
        setAppStep(APP_STEPS.ERROR);
      });
  };

  return (
    <div className={styles.root}>
      <div className={styles.body}>
        <div className={styles.rootPage}>
          <ErrorImage className={styles.logo} role="img" aria-label="Error image" />

          <Typography className={styles.title} variant="h6" align="center">{translate('accountAuth.retry.title')}</Typography>

          <Typography className={styles.description} variant="body1" align="center">{isSepa ? translate('accountAuth.sepaRetry.tryAgain') : translate('accountAuth.retry.tryAgain')}</Typography>
        </div>

        <div className={styles.buttonContainer}>
          {hasAutomaticFlow && (
            <Button
              className={styles.button}
              variant="filled"
              onClick={handleAddAutomatic}
              icon={
                isAutogiro && <BankIdLogo className={styles.bankIdButtonIcon} />
              }
              iconPosition="left"
              id="automatic-bank-account"
              isLoading={isAutomaticFlowLoading}
            >
              {translate('accountAuth.retry.selectAccountAgain')}
            </Button>
          )}

          <Button
            className={styles.button}
            color="primary"
            variant="outlined"
            onClick={handleAddManual}
            id="manual-bank-account"
            isLoading={isManualFlowLoading}
          >
            {translate('accountAuth.retry.singOnAnotherDevice')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Retry;
